import { useLayoutEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

interface Props {
  /**
   * Controls the expanded state from the outside. When provided, the hint opens
   * (and stays open) as long as this is true — used by the geocode-failure state
   * to surface the guidance exactly when it is needed. The user can still
   * collapse it manually afterwards.
   */
  forceOpen?: boolean;
  className?: string;
}

/** The rule lines rendered inside the expanded hint, in display order. */
const RULE_KEYS = [
  "form",
  "country",
  "house",
  "qualifiers",
  "index",
  "suggestions",
] as const;

/**
 * Collapsible "how to write an address" hint, shown next to every geocoding
 * address input (order create/edit, warehouse create).
 *
 * Modelled on the import modal's `orders.importModal.guide` block: collapsed by
 * default with a one-line summary visible, so it never disrupts the form layout.
 * Content is grounded in behaviour measured against live Nominatim (BUG-422).
 */
export function AddressHint({ forceOpen = false, className = "" }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  // Expand when the caller signals the guidance is needed (geocoding failed).
  // Only opens — never force-collapses, so a manual toggle is not overridden.
  // Layout effect (not a passive one) so the expansion is committed in the same
  // paint as the failure message, with no visible one-frame flash of the
  // collapsed state.
  useLayoutEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  return (
    <div
      className={`rounded-lg border border-gray-200 bg-gray-50/60 ${className}`}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left"
      >
        <span className="flex flex-col">
          <span className="text-xs font-semibold text-gray-700">
            {t("orders.addressHint.toggle")}
          </span>
          <span className="mt-0.5 text-[11px] text-gray-500">
            {t("orders.addressHint.summary")}
          </span>
        </span>
        <ChevronDown
          size={16}
          className={`shrink-0 text-gray-400 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>

      {open && (
        <div className="border-t border-gray-200 px-3 pb-3 pt-2">
          <ul className="list-disc space-y-1 pl-4 text-[11px] leading-relaxed text-gray-600">
            {RULE_KEYS.map((key) => (
              <li key={key}>{t(`orders.addressHint.rules.${key}`)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
